import { useEffect, useRef } from 'react'
import Portal from '~components/root/portal'
import Button from '~components/ui/button'
import Icon from '~components/ui/icon'
import useBlockScroll from '~hooks/use-block-scroll'
import { cn } from '~utils/cn'

import type { IntakeAlertLevel, IntakeSpecV2 } from '../intake-v2'

interface IntakeAlertProps {
  level: IntakeAlertLevel
  copy: IntakeSpecV2['alerts']
  onFindClinic: () => void
  onContinue: () => void
  // 버튼을 누르지 않고 화면에서 사라질 때 (뒤로 가기로 질문 화면이 바뀜)
  onDismiss?: () => void
}

/**
 * 응급 즉시 안내 (문진 v2, DESIGN 8절). 문구는 모두 질문지의 alerts 에서 온다.
 * 제출을 막지 않는다: "Continue and send to the vet" 으로 닫고 문진을 계속한다.
 */
const IntakeAlert = ({ level, copy, onFindClinic, onContinue, onDismiss }: IntakeAlertProps) => {
  useBlockScroll(true)
  const { title, body } = copy[level]
  const acted = useRef(false)
  const dismissRef = useRef(onDismiss)
  dismissRef.current = onDismiss

  useEffect(
    () => () => {
      if (!acted.current) dismissRef.current?.()
    },
    []
  )

  const act = (handler: () => void) => () => {
    acted.current = true
    handler()
  }

  return (
    <Portal id="dialog">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="intake-alert-title"
        aria-describedby="intake-alert-body"
        className="fixed inset-0 z-dialog flex flex-col bg-background"
      >
        <section className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-5 text-center">
          <Icon
            name="circle_exclamation"
            className={cn(
              'mb-4 size-12 shrink-0',
              level === 'emergency' ? 'fill-red-600' : 'fill-orange-400'
            )}
          />
          <h2
            id="intake-alert-title"
            className="mb-4 text-headline-3 text-gray-900 dark:text-white"
          >
            {title}
          </h2>
          <p id="intake-alert-body" className="text-body-1 text-gray-700 dark:text-gray-500">
            {body}
          </p>
        </section>
        <section className="flex w-full shrink-0 flex-col flex-gap-y-2 p-5">
          {/* 안내가 뜨면 가장 중요한 동작에 바로 초점 (스크린 리더·키보드) */}
          <Button className="w-full" autoFocus onClick={act(onFindClinic)}>
            {copy.actions.find_clinic}
          </Button>
          <Button
            className="w-full dark:border-primary-400 dark:text-primary-400"
            priority="secondary"
            onClick={act(onContinue)}
          >
            {copy.actions.continue}
          </Button>
        </section>
      </div>
    </Portal>
  )
}

export default IntakeAlert
